<script>
import { mapActions } from 'vuex'
import dayjs from '@/services/date'
import calendarMonthLayout from '@/utils/calendar-month-layout'
import {
  createSelectableMonths,
  Transitions,
  TransitionTypes,
  AvailabilityState,
} from '@/shared/constants/candidate/CandidateAvailability'

import CandidateAvailabilityCalendar from './candidate-availability-calendar.vue'

const {
  GoToPreviousMonth,
  GoToNextMonth,
  SelectEarlierMonth,
  SelectLaterMonth,
} = TransitionTypes

export default {
  name: 'CandidateAvailability',
  components: {
    CandidateAvailabilityCalendar,
  },
  props: {
    candidateId: {
      type: Number,
      required: true,
    },
    isActive: {
      type: Boolean,
      default: false,
    },
    bookingDate: {
      type: String,
      default: null,
    },
    bookingTimeZone: {
      type: String,
      default: null,
    },
  },
  data() {
    const selectableMonths = createSelectableMonths()

    return {
      selectableMonths,
      selectedMonthKey: selectableMonths.firstMonth,
      availabilityByMonth: {},
      timeZone: null,
      isLoading: false,
      isTransitioningToUncachedMonth: false,
      hasError: false,
      transitionType: null,
      activeRequestId: 0,
    }
  },
  computed: {
    monthSelectItems() {
      return this.selectableMonths.items
    },
    monthKeys() {
      return this.monthSelectItems.map((month) => month.value)
    },
    selectedMonth() {
      return this.selectableMonths.monthsByKey[this.selectedMonthKey]
    },
    selectedMonthIndex() {
      return this.monthKeys.indexOf(this.selectedMonthKey)
    },
    isAvailabilityLoading() {
      return this.isLoading || this.isTransitioningToUncachedMonth
    },
    availabilityTimeZone() {
      return this.bookingTimeZone || this.timeZone
    },
    timeZoneLabel() {
      return this.bookingTimeZone
        ? this.$t('candidateDetailsPopup.availability.yourTimeZoneLabel')
        : this.$t('candidateDetailsPopup.availability.timeZoneLabel')
    },

    transitionName() {
      return (
        Transitions[this.transitionType] || Transitions[TransitionTypes.Default]
      )
    },
  },
  watch: {
    isActive: {
      immediate: true,
      handler(active) {
        if (!active) return
        this.initSelectableMonths()
        this.loadMonth(this.selectedMonthKey)
      },
    },
    candidateId() {
      this.resetAvailabilityState()
    },
    bookingTimeZone() {
      this.resetAvailabilityState()
    },
  },
  methods: {
    ...mapActions({
      getCandidateAvailability: 'candidate/getCandidateAvailability',
    }),
    initSelectableMonths() {
      this.selectableMonths = createSelectableMonths()
      const bookingMonth = dayjs(this.bookingDate).format('YYYY-MM')
      const selectedMonth = this.selectableMonths.items.find(
        ({ date }) => date.format('YYYY-MM') === bookingMonth
      )

      this.selectedMonthKey =
        selectedMonth?.value ?? this.selectableMonths.firstMonth
    },
    resetAvailabilityState() {
      this.activeRequestId += 1
      this.availabilityByMonth = {}
      this.hasError = false
      this.isLoading = false
      this.isTransitioningToUncachedMonth = false
      this.transitionType = null
      this.timeZone = null

      if (this.isActive) {
        this.initSelectableMonths()
        this.loadMonth(this.selectedMonthKey)
      }
    },
    getTransitionType(monthKey, moveEarlier, moveLater) {
      const isEarlierMonth =
        this.monthKeys.indexOf(monthKey) < this.selectedMonthIndex

      return isEarlierMonth ? moveEarlier : moveLater
    },

    handleMonthSelected(monthKey) {
      if (monthKey === this.selectedMonthKey) return

      const transitionType = this.getTransitionType(
        monthKey,
        SelectEarlierMonth,
        SelectLaterMonth
      )

      this.goToMonth(monthKey, transitionType)
    },
    goToMonth(monthKey, transitionType) {
      if (monthKey === undefined) return

      this.transitionType =
        transitionType ||
        this.getTransitionType(monthKey, GoToPreviousMonth, GoToNextMonth)

      this.isTransitioningToUncachedMonth = !this.availabilityByMonth[monthKey]
      this.selectedMonthKey = monthKey

      this.loadMonth(monthKey)
    },
    handleMonthTransitionComplete() {
      this.isTransitioningToUncachedMonth = false
      this.transitionType = null
    },
    candidateAvailabilityMapper(monthDate, timeZone, availableDates = []) {
      const { Empty, Available, Unavailable } = AvailabilityState

      const availableDateKeys = new Set(
        availableDates.map((date) =>
          dayjs(date)
            .tz(timeZone)
            .format('YYYY-MM-DD')
        )
      )

      return calendarMonthLayout(monthDate).map((cell) => ({
        ...cell,
        state: cell.isoDate
          ? availableDateKeys.has(cell.isoDate)
            ? Available
            : Unavailable
          : Empty,
      }))
    },
    async loadMonth(monthKey) {
      const requestId = ++this.activeRequestId

      if (this.availabilityByMonth[monthKey]) {
        this.isLoading = false
        this.hasError = false
        return
      }

      const monthStart = this.selectableMonths.monthsByKey[monthKey].date

      this.isLoading = true
      this.hasError = false

      const { data, error } = await this.getCandidateAvailability({
        candidateId: this.candidateId,
        startDate: monthStart.format('YYYY-MM-DD'),
        endDate: monthStart.endOf('month').format('YYYY-MM-DD'),
        timeZone: this.bookingTimeZone,
      })

      // Navigation and candidate changes invalidate earlier requests.
      if (requestId !== this.activeRequestId) return

      if (error) {
        this.isLoading = false
        this.hasError = true
        return
      }

      this.timeZone = data.timeZone

      this.availabilityByMonth = {
        ...this.availabilityByMonth,
        [monthKey]: this.candidateAvailabilityMapper(
          monthStart,
          this.availabilityTimeZone,
          data.availableDates
        ),
      }

      this.isLoading = false
    },
  },
}
</script>

<template>
  <div>
    <div class="d-flex align-center justify-center">
      <v-btn
        :class="{ 'pointer-events-none opacity-0': selectedMonth.isFirst }"
        class="transition-opacity"
        icon
        :aria-label="$t('candidateDetailsPopup.availability.previousMonth')"
        @click="goToMonth(monthKeys[selectedMonthIndex - 1])"
      >
        <v-icon size="20">mdi-chevron-left</v-icon>
      </v-btn>

      <BaseSelectMenu
        :value="selectedMonthKey"
        :items="monthSelectItems"
        :aria-label="$t('candidateDetailsPopup.availability.monthSelectLabel')"
        item-text="label"
        item-value="value"
        :transition-name="transitionName"
        class="month-select mx-2"
        @change="handleMonthSelected"
      />

      <v-btn
        :class="{ 'pointer-events-none opacity-0': selectedMonth.isLast }"
        class="transition-opacity"
        icon
        :aria-label="$t('candidateDetailsPopup.availability.nextMonth')"
        @click="goToMonth(monthKeys[selectedMonthIndex + 1])"
      >
        <v-icon size="20">mdi-chevron-right</v-icon>
      </v-btn>
    </div>

    <CandidateAvailabilityCalendar
      :selected-month="selectedMonth"
      :availability="availabilityByMonth[selectedMonthKey]"
      :is-loading="isAvailabilityLoading"
      :has-error="hasError"
      :transition-name="transitionName"
      @retry="loadMonth(selectedMonthKey)"
      @monthTransitionComplete="handleMonthTransitionComplete"
    />

    <div
      v-if="!hasError"
      class="d-flex align-center justify-space-between flex-wrap mt-4 text-md"
    >
      <div
        :class="availabilityTimeZone ? 'opacity-100' : 'opacity-0'"
        class="d-flex align-center transition-opacity"
      >
        <v-icon size="18" class="mr-1">mdi-earth</v-icon>
        <span class="mr-1">{{ timeZoneLabel }}</span>
        <span>{{ availabilityTimeZone }}</span>
      </div>

      <span class="d-flex align-center">
        <span class="availability-legend semantics-success-50" />
        {{ $t('candidateDetailsPopup.availability.available') }}
        <span class="availability-legend semantics-error-50 ml-4" />
        {{ $t('candidateDetailsPopup.availability.unavailable') }}
      </span>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/spacing';

.month-select {
  max-width: 200px;
}

.availability-legend {
  display: inline-block;
  width: $spacing-sm;
  height: $spacing-sm;
  margin-right: 6px;
  border-radius: $spacing-4xs;
}
</style>
